import {assert} from 'Asserts';
import {FieldDefinitionType, TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  Method,
  MethodNode,
  ProcedureDataParameterPlannedValue,
  ProcedureParameterDataTypeDefinition,
} from 'procedures/ProcedureDomainTypes';
import {
  folderItemTypeToTagSchemaItemType,
  tagSchemaItemTypeToFieldDefinitionType,
} from 'procedures/shared-components/schema-field-type-selectors/materialTypeCategory';
import {FieldDefinitionGraphQL} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNodeSectionData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {fieldTypeToFieldDefinitionType} from 'schemas/fields/FieldDefinitionCompatibilityUtils';
import {
  FieldType,
  isRequiredLinkCustomSchemaInterface,
  isRequiredLinkFolderItemType,
  isRequiredLinkTagSchema,
} from 'schemas/fields/SchemaFieldConstants';
import {assertNever} from 'util/Types';

export interface ProcedureFieldInfo {
  cid: string;
  name: string;
  dataType: ProcedureParameterDataTypeDefinition;
  // Setpoints for data parameters
  values?: Array<ProcedureDataParameterPlannedValue>;
  // Stable identifier across all versions of a parameter. Null until parameter is persisted.
  procedureParameterId: string | null;
}

/**
 * Field group keys used to categorize procedure fields.
 * Keep in sync with ProcedureFieldGroupKey in
 * benchling/procedures/controller/processes_platform/procedure_method_task_execution_type_adapter.py
 */
export enum ProcedureFieldGroupKey {
  MATERIAL_INPUTS = 'MATERIAL_INPUTS',
  MATERIAL_OUTPUTS = 'MATERIAL_OUTPUTS',
  DATA_PARAMETERS = 'DATA_PARAMETERS',
  EQUIPMENT = 'EQUIPMENT',
}

/**
 * Returns a stable field client identifier for a procedure parameter.
 *
 * Uses the procedure parameter ID (stable across renames) when available.
 * Returns an identifier that is unique within the field group as a fallback.
 * This fallback identifier is safe to use because for Procedures application, we don't rely on storing the
 * field identifiers within the processes flowchart system, our parameters are stored in our PMDV application data.
 * So this fallback identifier is only used transiently in the UI until we receive the updated fields with their
 * procedure parameter IDs. See ProcedureMethodNodeAdapter:updateTaskDefinitionVersionWithApplicationData to
 * understand how we replace the input and output field definitions within the PDTV with each change made to the flowchart.
 *
 * Keep in sync with the backend:
 * ParameterSettingToWorkflowFieldDefinitionMapper.resolve_field_client_identifier
 */
export function getFieldClientId(field: ProcedureFieldInfo, fieldGroupKey: ProcedureFieldGroupKey): string {
  if (field.procedureParameterId != null) {
    return field.procedureParameterId;
  }
  return `${fieldGroupKey}/${field.name}`;
}

interface SchemaLinkResolution {
  schema: {__typename: 'TagSchemaNode'; id: string; itemType: TagSchemaItemType} | null;
  type: FieldDefinitionType;
}

/**
 * Resolves the schema and effective FieldDefinitionType for a BIOENTITY_LINK field.
 *
 * For named schema constraints: returns the real TagSchemaNode so that resolveTypeFromSchemaLink
 * can extract the itemType for precise type checking AND areLinkSchemasCompatible can match IDs.
 *
 * For fieldset constraints: returns schema: null and overrides the type to the FieldDefinitionType
 * implied by the fieldset's itemType (e.g., DNA Sequence fieldset → DNA_SEQUENCE_LINK). The itemType
 * is carried on the link itself, so no fieldset lookup is needed. We resolve via `type` rather than
 * emitting an `interface` node because this produces a `SchemaLinkFieldDefinitionNode`, and
 * FieldDefinitionCompatibilityUtils resolves that typename through `resolveTypeFromSchemaLink` (which
 * reads `schema`/`type`, never `interface`). A fieldset with no base bioentity type (itemType null)
 * keeps the base ENTITY_LINK type. This matches the folder-item-type handling below.
 *
 * For folder-item-type constraints (e.g., "Custom entity"): returns schema: null (so
 * areLinkSchemasCompatible passes without comparing IDs) and overrides the type to the specific
 * FieldDefinitionType (so resolveTypeFromSchemaLink falls back to the correct type from field.type).
 *
 * For unconstrained (Any entity): returns schema: null with the base ENTITY_LINK type.
 */
export function resolveSchemaLinkFieldType(
  requiredLink: ProcedureParameterDataTypeDefinition['requiredLink'],
  baseType: FieldDefinitionType
): SchemaLinkResolution {
  if (isRequiredLinkTagSchema(requiredLink)) {
    const tagSchema = requiredLink.tagSchema;
    return {
      schema: {
        __typename: 'TagSchemaNode' as const,
        id: tagSchema.id,
        itemType: folderItemTypeToTagSchemaItemType(tagSchema.folderItemType),
      },
      type: baseType,
    };
  }
  if (isRequiredLinkCustomSchemaInterface(requiredLink)) {
    const {itemType} = requiredLink.schemaInterface;
    return {
      schema: null,
      type: itemType != null ? tagSchemaItemTypeToFieldDefinitionType(itemType) : baseType,
    };
  }
  if (isRequiredLinkFolderItemType(requiredLink)) {
    return {
      schema: null,
      type: tagSchemaItemTypeToFieldDefinitionType(
        folderItemTypeToTagSchemaItemType(requiredLink.folderItemType)
      ),
    };
  }
  return {schema: null, type: baseType};
}

/**
 * Converts a procedure field (procedure field format) to a field definition (PTDV format).
 *
 * Keep in sync with backend: `benchling/domain_graph/mappers/platform/field_definition_type_resolver.py`
 */
export function createFieldDefinitionGraphQL(
  field: ProcedureFieldInfo,
  fieldGroupKey: ProcedureFieldGroupKey
): FieldDefinitionGraphQL {
  const fieldClientIdentifier = getFieldClientId(field, fieldGroupKey);
  const fieldType = field.dataType.type;
  const fieldDefinitionType = fieldTypeToFieldDefinitionType(fieldType);

  const baseFieldDefinition = {
    id: field.cid,
    name: field.name,
    isMulti: false,
    isRequired: false,
    type: fieldDefinitionType,
    archiveData: {
      __typename: 'ArchiveDataNode' as const,
      archived: false,
    },
  };

  const fieldDefinition =
    fieldType === FieldType.BIOENTITY_LINK
      ? (() => {
          const {schema, type} = resolveSchemaLinkFieldType(field.dataType.requiredLink, fieldDefinitionType);
          return {
            __typename: 'SchemaLinkFieldDefinitionNode' as const,
            ...baseFieldDefinition,
            type,
            schema,
            interface: null,
            folderItemType: null,
          };
        })()
      : {
          __typename: 'BaseFieldDefinitionNode' as const,
          ...baseFieldDefinition,
        };

  return {
    __typename: 'ProcessFieldDefinitionNode' as const,
    fieldIdentifier: fieldClientIdentifier,
    fieldClientIdentifier,
    fieldGroupKey,
    fieldDefinition,
  };
}

/**
 * Gets fields for a specific procedure field group from the task definition version.
 * Material outputs use outputFieldDefinitions, all others use inputFieldDefinitions.
 */
export function getFieldsForGroup(
  methodDefinitionVersion: Method,
  fieldGroupKey: ProcedureFieldGroupKey
): ReadonlyArray<ProcedureFieldInfo> {
  switch (fieldGroupKey) {
    case ProcedureFieldGroupKey.MATERIAL_OUTPUTS:
      return methodDefinitionVersion.materialOutputSettings.map(({materialOutputVersion}) => ({
        ...materialOutputVersion,
        procedureParameterId: materialOutputVersion.procedureMaterialOutputId,
      }));
    case ProcedureFieldGroupKey.MATERIAL_INPUTS:
      return methodDefinitionVersion.materialInputSettings.map(({materialInputVersion}) => ({
        ...materialInputVersion,
        procedureParameterId: materialInputVersion.procedureMaterialInputId,
      }));
    case ProcedureFieldGroupKey.DATA_PARAMETERS:
      return methodDefinitionVersion.dataParameterSettings.map(({dataParameterVersion, values}) => ({
        ...dataParameterVersion,
        values,
        procedureParameterId: dataParameterVersion.procedureDataParameterId,
      }));
    case ProcedureFieldGroupKey.EQUIPMENT:
      return methodDefinitionVersion.equipmentSettings.map(({equipmentVersion}) => ({
        ...equipmentVersion,
        procedureParameterId: equipmentVersion.procedureEquipmentId,
      }));
    default:
      assert(false, `Unknown field group key: ${assertNever(fieldGroupKey)}`);
      return [];
  }
}

/**
 * Creates input and output field definitions from method application data.
 */
export function createFieldDefinitionsFromMethod(applicationData: Method): {
  inputFieldDefinitions: Array<FieldDefinitionGraphQL>;
  outputFieldDefinitions: Array<FieldDefinitionGraphQL>;
} {
  const inputFieldDefinitions: Array<FieldDefinitionGraphQL> = getFieldsForGroup(
    applicationData,
    ProcedureFieldGroupKey.MATERIAL_INPUTS
  ).map((field) => createFieldDefinitionGraphQL(field, ProcedureFieldGroupKey.MATERIAL_INPUTS));

  const outputFieldDefinitions: Array<FieldDefinitionGraphQL> = getFieldsForGroup(
    applicationData,
    ProcedureFieldGroupKey.MATERIAL_OUTPUTS
  ).map((field) => createFieldDefinitionGraphQL(field, ProcedureFieldGroupKey.MATERIAL_OUTPUTS));

  return {inputFieldDefinitions, outputFieldDefinitions};
}

/**
 * Updates the clientIds of section rows to match current procedure parameter IDs.
 *
 * After a save, procedure parameter IDs get assigned by the server. The sections
 * (which are used as ReactFlow handle sources) need their clientIds updated so that
 * edges can correctly connect to the right handles.
 */
export function updateSectionClientIds(
  sections: Array<TaskSchemaNodeSectionData>,
  applicationData: MethodNode
): Array<TaskSchemaNodeSectionData> {
  const fieldGroupKeys = Object.values(ProcedureFieldGroupKey);
  return sections.map((section, sectionIndex) => {
    const fieldGroupKey = fieldGroupKeys[sectionIndex];
    if (fieldGroupKey == null) {
      return section;
    }
    const methodFields = getFieldsForGroup(applicationData, fieldGroupKey);
    return {
      ...section,
      sectionRows: section.sectionRows.map((row, rowIndex) => {
        const field = methodFields[rowIndex];
        if (field == null) {
          return row;
        }
        return {
          ...row,
          clientId: getFieldClientId(field, fieldGroupKey),
        };
      }),
    };
  });
}
